import { differenceInMinutes } from 'date-fns';
import _ from 'lodash';
import { useCallback, useEffect, useMemo } from 'react';
import isEqual from 'react-fast-compare';
import { RootState, useDispatch, useSelector } from 'store';
import {
  partDataSelector,
  removePartDataByIdAndSchema,
  selectPartsByIds,
} from 'store/slices/apiV1/partData';
import {
  createOrUpdatePartsStatus,
  getPartStatusSelector,
  removePartStatusByIdAndSchema,
} from 'store/slices/apiV1/partStatus';
import { PartId } from 'types/part';
import {
  DoneLoadingStatusTypes,
  LoadingStatusTypes,
  PartSchemas,
  partStatusRequestTypes,
} from 'types/partStatus';

interface UseGetPartListProps {
  partIds: string[];
  schemas: PartSchemas[];
  useLazyRequest?: boolean;
  cacheKey: string;
}

// Helper function to get part status for a specific part ID
const getPartStatusForPartId = (
  partStatus: Record<
    string,
    Record<PartSchemas, partStatusRequestTypes | null>
  >,
  partId: string
) => partStatus?.[partId] || {};

// Helper function to get part data for a specific part ID
const getPartDataForPartId = (
  partData: Record<string, Record<PartSchemas, any>>,
  partId: string
) => partData?.[partId];

// Helper function to calculate loading states for a specific part ID
const getPartDataIsLoading = (
  partStatusForPartId: Record<PartSchemas, partStatusRequestTypes | null>
) =>
  _.reduce(
    Object.keys(partStatusForPartId),
    (acc, key) => {
      if (key) {
        const partStatusForKey = partStatusForPartId[key as PartSchemas];
        acc[key] = partStatusForKey
          ? LoadingStatusTypes.includes(
              partStatusForKey as partStatusRequestTypes
            )
          : false;
      }
      return acc;
    },
    {} as { [key: PartId]: boolean }
  );

export default function useRequestDecoratedPartData({
  partIds = [],
  schemas,
  useLazyRequest = false,
  cacheKey,
}: UseGetPartListProps) {
  const dispatch = useDispatch();

  const selectPartStatus = useSelector(
    (state: RootState) => getPartStatusSelector(state),
    isEqual
  );
  const selectPartData = useSelector((state: RootState) =>
    partDataSelector(state)
  );

  const partStatusForPartIds = useMemo(() => {
    if (partIds) {
      return _.pickBy(selectPartStatus, (_value, key) => partIds.includes(key));
    }
    return {};
  }, [selectPartData, partIds]);

  const cachedParts = useSelector(
    (state) => selectPartsByIds(state, partIds, cacheKey),
    isEqual
  );

  const dataIsExpired = ({
    fetchedAt,
    schema,
  }: {
    fetchedAt: Date;
    schema: PartSchemas;
  }) => {
    if (!fetchedAt) {
      return true;
    }
    switch (schema) {
      case PartSchemas.market:
      case PartSchemas.supply:
        //   24 hours
        return differenceInMinutes(new Date(), fetchedAt) > 24 * 60;
      case PartSchemas.core:
      case PartSchemas.specs:
      case PartSchemas.alts:
      case PartSchemas.suggestions:
        //   7 days
        return differenceInMinutes(new Date(), fetchedAt) > 24 * 60 * 7;
      default:
        return true;
    }
  };

  const requestParts = (schema: PartSchemas) => {
    // only request parts that haven't already been requested, or whose data is expired
    const partsToFetch = _.chain(partIds)
      .compact()
      .uniq()
      .filter((partId) => {
        const fetchedAt = cachedParts?.[partId]?.[schema]?.fetchedAt;
        const isExpired =
          fetchedAt &&
          dataIsExpired({
            fetchedAt: new Date(fetchedAt),
            schema,
          });

        if (isExpired) {
          dispatch(removePartStatusByIdAndSchema({ partId, schema }));
          dispatch(
            removePartDataByIdAndSchema({
              partIds: [partId],
              schema,
            })
          );
        }
        return ![...LoadingStatusTypes, ...DoneLoadingStatusTypes].includes(
          selectPartStatus?.[partId]?.[schema] as partStatusRequestTypes
        );
      })
      .value();

    if (partsToFetch.length > 0) {
      dispatch(
        createOrUpdatePartsStatus({
          partIds: partsToFetch,
          schemas: [schema],
          statusType: partStatusRequestTypes.READY,
        })
      );
    }
  };

  const requestForAllSchemas = () => {
    _.forEach(schemas, (schema) => {
      requestParts(schema);
    });
  };

  useEffect(() => {
    if (!useLazyRequest && partIds?.length > 0) {
      requestForAllSchemas();
    }
  }, [partIds]);

  const isSuccess = useMemo(
    () =>
      _.every(partIds, (partId) =>
        _.every(
          schemas,
          (schema) =>
            partStatusForPartIds?.[partId]?.[schema] ===
            partStatusRequestTypes.SUCCESS
        )
      ),
    [cachedParts, partStatusForPartIds]
  );

  const getSuccessBySchema = (schemasToCheck: PartSchemas[]) =>
    _.every(partIds, (partId) =>
      _.every(
        schemasToCheck,
        (schema) =>
          partStatusForPartIds?.[partId]?.[schema] ===
          partStatusRequestTypes.SUCCESS
      )
    );

  // Helper function to get derived data for a specific part ID
  const getPartDataForId = useCallback(
    (partId: string) => {
      const partStatusForPartId = getPartStatusForPartId(
        partStatusForPartIds,
        partId
      );
      const describePartData = getPartDataForPartId(cachedParts, partId);
      const partDataIsLoading = getPartDataIsLoading(partStatusForPartId);

      return {
        partStatusForPartId,
        describePartData,
        partDataIsLoading,
      };
    },
    [partStatusForPartIds, cachedParts]
  );

  return {
    lazyRequestParts: requestForAllSchemas,
    // list of data by partId for each requested schema
    data: cachedParts,
    // memoized list of loading state for requested schema
    status: partStatusForPartIds,
    isSuccess,
    getSuccessBySchema,
    // Helper function to get derived data for a specific part ID
    getPartDataForId,
  };
}
